// @ts-nocheck
import { useLeafletContext } from '@react-leaflet/core';
import L from 'leaflet';
import 'leaflet.icon.glyph';
import { useMap } from 'react-leaflet';
import { useSelector } from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { getRouteBounds } from 'views/screens/Routes/utils';
import wkx from 'wkx';
import { useRef, useEffect } from 'react';

export interface MapObjectsBoundsOptions {
  isSelectedSomething?: any;
  setMapPositionToGeofenceBounds?: boolean;
  setMapPositionToLocationBounds?: boolean;
  setMapPositionToUnitBounds?: boolean;
  setMapPositionToUnitPositionsBounds?: boolean;
  setMapPositionToRouteBounds?: boolean;
  geofences?: any[];
  locations?: any[];
  units?: any[];
  routes?: any[];
  rerenderMapObjectBounds?: date;
  paddingBottomRight?: any[];
  paddingTopLeft?: any[];
  preventAutoZoom?: boolean; // 🚀 Nueva propiedad para controlar auto-zoom
}

const MapObjectsBounds = (props: MapObjectsBoundsOptions) => {
  const context = useLeafletContext();
  const lastBoundsUpdateRef = useRef<Date | null>(null);
  const boundsThrottleRef = useRef<NodeJS.Timeout | null>(null);

  const {
    isSelectedSomething,
    setMapPositionToGeofenceBounds = false,
    setMapPositionToLocationBounds = false,
    setMapPositionToUnitBounds = false,
    setMapPositionToUnitPositionsBounds = false,
    setMapPositionToRouteBounds = false,
    geofences = [],
    locations = [],
    units = [],
    routes = [],
    rerenderMapObjectBounds,
    paddingBottomRight,
    paddingTopLeft,
    preventAutoZoom = false // 🚀 Control de auto-zoom
  } = props;
  const store = useSelector((state) => state);
  const map = useMap();

  const zoomMapToObjects = () => {
    // 🚀 SOLUCION: Prevenir auto-zoom si está deshabilitado
    if (preventAutoZoom) {
      return;
    }

    // 🚀 SOLUCION: Throttle de zoom para evitar flyToBounds excesivos
    const now = new Date();
    if (lastBoundsUpdateRef.current && 
        now.getTime() - lastBoundsUpdateRef.current.getTime() < 1000) {
      return; // No hacer zoom si ha pasado menos de 1 segundo
    }

    var bounds = [];
    if (setMapPositionToLocationBounds) {
      locations
        .filter((location) => location?.latitude != null && location?.longitude != null)
        .map((location) => {
          bounds = [...bounds, L.latLng(location.latitude, location.longitude)];
        });
    }
    if (setMapPositionToGeofenceBounds) {
      geofences.map((geofence) => {
        let geofenceGeoJSON;
        geofenceGeoJSON = WKBtoGeoJSON(geofence.geofenceWktString);
        if (geofence.shape == 'C') {
          bounds = [
            ...bounds,
            L.latLng(geofenceGeoJSON.coordinates[1], geofenceGeoJSON.coordinates[0])
          ];
        } else if (geofence.shape == 'R') {
          bounds = [
            ...bounds,
            ...geofenceGeoJSON.coordinates[0].map((coords) => L.latLng([coords[1], coords[0]]))
          ];
        } else if (geofence.shape == 'P') {
          bounds = [
            ...bounds,
            ...(geofenceGeoJSON.type == 'LineString'
              ? geofenceGeoJSON.coordinates.map((coords) => L.latLng([coords[1], coords[0]]))
              : geofenceGeoJSON.coordinates[0].map((coords) => L.latLng([coords[1], coords[0]])))
          ];
        } else if (geofence.shape == 'U') {
          bounds = [
            ...bounds,
            _.flatten(
              geofenceGeoJSON.coordinates.map((coordinate) =>
                coordinate.map((coords) =>
                  coords[1] && coords[0] ? L.latLng([coords[1], coords[0]]) : null
                )
              )
            )
          ];
        }
      });
    }
    if (setMapPositionToUnitBounds) {
      units
        .filter((unit) => unit?.latitude != null && unit?.longitude != null)
        .map((unit) => {
          bounds = [...bounds, L.latLng(unit.latitude, unit.longitude)];
        });
    }
    if (setMapPositionToUnitPositionsBounds) {
      units
        .filter((unit) => unit?.historicalPositions?.polyline)
        .map((unit) => {
          unit.historicalPositions.polyline.map((position) => {
            bounds = [...bounds, L.latLng(position.latitude, position.longitude)];
          });
        });
    }
    if (setMapPositionToRouteBounds) {
      routes.map((route) => {
        bounds = [...bounds, ...getRouteBounds(route)];
      });
    }
    if (
      (setMapPositionToGeofenceBounds ||
        setMapPositionToLocationBounds ||
        setMapPositionToUnitBounds ||
        setMapPositionToUnitPositionsBounds ||
        setMapPositionToRouteBounds) &&
      bounds.length > 0
    ) {
      // 🚀 SOLUCION: Actualizar timestamp del último zoom
      lastBoundsUpdateRef.current = now;

      // ✅ Limpiar timeout anterior si existe
      if (boundsThrottleRef.current) {
        clearTimeout(boundsThrottleRef.current);
      }

      // ✅ Aplicar zoom con throttle
      boundsThrottleRef.current = setTimeout(() => {
        try {
          const validBounds = bounds.filter((bound) => bound != null && bound instanceof L.LatLng);
          
          // 🚀 NUEVO: Si es solo una unidad, centrarla con zoom apropiado
          if (setMapPositionToUnitBounds && validBounds.length === 1 && !setMapPositionToUnitPositionsBounds) {
            map.setView(validBounds[0], 9, {
              animate: true,
              duration: 0.5
            });
          } else {
            // Para múltiples puntos o polilíneas, usar flyToBounds
            map.flyToBounds(
              L.latLngBounds(validBounds),
              {
                paddingBottomRight: paddingBottomRight ?? undefined,
                paddingTopLeft: paddingTopLeft ?? undefined,
                maxZoom: 15,
                animate: 15 !== map.getZoom(),
                duration: 0.5 // ✅ Reducir duración para transiciones más suaves
              }
            );
          }
        } catch (error) {
          console.warn('MapObjectsBounds: Error en flyToBounds:', error);
        }
      }, 100); // Delay de 100ms para suavizar
    }
  };

  const WKBtoGeoJSON = (wkt) => {
    // let wkbBuffer = new Buffer(hex);
    let geometry = wkx.Geometry.parse(wkt);

    return geometry.toGeoJSON();
  };
  useDeepCompareEffect(() => {
    if (
      isSelectedSomething == null ||
      Object.values(isSelectedSomething).filter((s) => s).length === 0
    )
      zoomMapToObjects();
  }, [
    isSelectedSomething,
    geofences,
    locations,
    setMapPositionToGeofenceBounds,
    rerenderMapObjectBounds
  ]);

  // 🚀 SOLUCION: Cleanup de timeouts para evitar memory leaks
  useEffect(() => {
    return () => {
      if (boundsThrottleRef.current) {
        clearTimeout(boundsThrottleRef.current);
      }
    };
  }, []);
  return null;
};

MapObjectsBounds.defaultProps = {
  setMapPositionToGeofenceBounds: false,
  setMapPositionToLocationBounds: false,
  geofences: [],
  locations: []
};

export default MapObjectsBounds;
